import cl from 'clsx'
import { ChangeEventHandler, KeyboardEventHandler } from 'react'

import CheckIcon from '@synapex/frontend-shared/icons/check-15x12.svg?react'
import CloseIcon from '@synapex/frontend-shared/icons/close-12.svg?react'
import PlusIcon from '@synapex/frontend-shared/icons/plus-13.svg?react'
import TrashIcon from '@synapex/frontend-shared/icons/trash-14x16.svg?react'

import { Button2, IconButton } from '@/true-fsd/shared/ui/ButtonLink'

import classes from './ShortUrlInput.module.scss'

import { useCopyToClipboard } from '@/shared/hooks'
import { Toast } from '@/shared/ui'
import { UseShortUrlInput } from './hook'

type PlaceholderProps = Pick<UseShortUrlInput, 'onInput'> & {
    disabled: boolean
}
const Placeholder = ({ onInput, disabled }: PlaceholderProps) => {
    return (
        <Button2
            disabled={disabled}
            className={classes.addButton}
            SuffixIcon={PlusIcon}
            onClick={onInput}
        >
            Добавить сокращенную ссылку
        </Button2>
    )
}

type UseSeparateInputArgs = {
    onChange: (v: string) => void
    onApply: () => void
}
const useSeparateInput = (props: UseSeparateInputArgs) => {
    const { onApply } = props

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (evt) => {
        switch (evt.key) {
            case 'Enter': {
                onApply()
                // evt.currentTarget.blur()
                return
            }
        }
    }

    // const onBlur: FocusEventHandler<HTMLInputElement> = onApply

    const onChange: ChangeEventHandler<HTMLInputElement> = (evt) => {
        props.onChange(evt.target.value)
    }

    return {
        onChange,
        onKeyDown,
        // onBlur,
    }
}

type InputProps = Pick<
    UseShortUrlInput,
    'value' | 'onApply' | 'onCancel' | 'onChange' | 'errorMessage'
>
const Input = ({
    value,
    onApply,
    onChange,
    errorMessage,
    onCancel,
}: InputProps) => {
    const inputProps = useSeparateInput({ onApply, onChange })
    return (
        <div
            className={cl(classes.inputWrapper, {
                [classes.error]: Boolean(errorMessage),
            })}
        >
            <div className={cl(classes.linkText, classes.inputRow)}>
                <input
                    {...inputProps}
                    value={value}
                    className={cl(classes.input)}
                />

                <IconButton
                    Icon={CheckIcon}
                    onClick={onApply}
                    iconClassName={classes.checkIcon}
                />

                <IconButton
                    Icon={CloseIcon}
                    onClick={onCancel}
                    iconClassName={classes.closeIcon}
                />
            </div>
            {errorMessage && (
                <div className={classes.status}>
                    <span>{errorMessage}</span>
                </div>
            )}
        </div>
    )
}

type DisplayProps = Pick<UseShortUrlInput, 'onClear' | 'value'>
const Display = ({ onClear, value }: DisplayProps) => {
    const { copy, hideToast, toastMessage, toastVisible } = useCopyToClipboard()

    return (
        <>
            <div className={cl(classes.linkText, classes.display)}>
                <div onClick={() => copy(value)} className={classes.clickCopy}>
                    {value}
                </div>

                {/* <DeleteButton
                    className={classes.deleteButton}
                    onDelete={onClear}
                    closeIconClassName={classes.closeIcon}
                /> */}

                <IconButton
                    onClick={onClear}
                    Icon={TrashIcon}
                    className={classes.deleteButton}
                />
            </div>
            <Toast
                message={toastMessage}
                visible={toastVisible}
                onClose={hideToast}
                greenSwooshIcon={false}
            />
        </>
    )
}

export type ShortUrlInputProps = {
    className?: string
    disabled: boolean
} & Pick<
    UseShortUrlInput,
    | 'value'
    | 'onChange'
    | 'errorMessage'
    | 'onCancel'
    | 'onApply'
    | 'onClear'
    | 'inputEnabled'
    | 'onInput'
>
export const ShortUrlInput = ({
    className,
    inputEnabled,
    value,
    onChange,
    errorMessage,
    onCancel,
    onApply,
    onClear,
    onInput,
    disabled,
}: ShortUrlInputProps) => {
    const content = (() => {
        if (inputEnabled) {
            return (
                <Input
                    {...{ value, onChange, errorMessage, onCancel, onApply }}
                />
            )
        }

        if (value) {
            return <Display {...{ onClear, value }} />
        }

        return <Placeholder disabled={disabled} {...{ onInput }} />
    })()

    return <div className={cl(classes.root, className)}>{content}</div>
}
